import { z } from 'zod';

import { LaunchpadId } from '@/constants/launchpads';

import { SUPPORTED_CHAIN_IDS } from '../../constants/chain';
import { deserializeDiscoveryState } from './utils';

export type DiscoveryTokenResult = {
  token: {
    address: string;
    name: string;
    logo: string | null;
    symbol: string;
    chainId: number;
    decimals: number;
  };
  launchpad?: string;
  timeframe: '5m' | '1' | '4' | '12' | '24';
  price: string;
  change5m: string;
  change1h: string;
  change4h: string;
  change12h: string;
  change24h: string;
  change7d: string | null;
  change30d: string | null;
  // ISO String
  age: string | null;
  liquidity: string;
  marketCap: string | null;
  circulatingMarketCap?: string | null;
  volByTime: string;
  numTxsByTime: string;
  numHolders: string;
  issuer?: {
    name: string;
    logo: string | null;
  };
  backing?: string | null;
  currency?: string | null;
  liquidityScore?: number;
  isBridged: boolean | null;
};

export type Asset = {
  displayName: string;
  symbol: string;
  logo: string;
  tokens: DiscoveryTokenResult[];
  price: number;
  marketCap: number;
  volume: number;
  change1h: number;
  change24h: number;
  change7d: number;
  change30d: number;
  maxLiquidityScore?: number | null;
};

export type UnifiedDiscoveryResultItem = Asset | DiscoveryTokenResult;

export function isAsset(item: Asset | DiscoveryTokenResult | Record<string, never>): item is Asset {
  return (
    item !== undefined &&
    item !== null &&
    typeof item === 'object' &&
    'displayName' in item &&
    'tokens' in item &&
    Array.isArray((item as Asset).tokens)
  );
}

export type DiscoveryZoraCreatorResult = {
  name: string;
  address: string;
  logo: string;
};

export type DiscoveryIssuerResult = {
  name: string;
  logo: string | null;
};

type SuccessType = {
  type: 'success';
  data: UnifiedDiscoveryResultItem[] | null;
  totalCount?: number;
};

export type ErrorType = {
  type: 'error';
  fallbackResult: DiscoveryTokenResult[] | null;
};

export type ResultType = SuccessType | ErrorType;

const sortableColumns = [
  'trendingScore',
  'price',
  'change5m',
  'change1h',
  'change4h',
  'change12h',
  'change24h',
  'change7d',
  'change30d',
  'age',
  'numTxsByTime',
  'liquidity',
  'marketCap',
  'volByTime',
  'numHolders',
  'currency',
  'issuer',
  'backing',
] as const;

export const filterableNumericColumns = [
  'liquidity',
  'marketCap',
  'volByTime',
  'numTxsByTime',
  'age',
  'numHolders',
  'change5m',
  'change1',
  'change4',
  'change12',
  'change24',
] as const;

export const aggregatedPresets = ['currencies', 'lsts', 'wrapped', 'commodities'] as const;

export const NEON_DB_TOKENS_BACKED_PRESETS = ['popular', ...aggregatedPresets, 'stocks'] as const;

export const UNIFIED_DISCOVERY_PRESETS = [
  'popular',
  'currencies',
  'wrapped',
  'lsts',
  'commodities',
  'stocks',
] as const;

export type NeonDbTokensBackedPreset = (typeof NEON_DB_TOKENS_BACKED_PRESETS)[number];

export function isNeonDbTokensBackedPreset(
  preset: string | undefined,
): preset is NeonDbTokensBackedPreset {
  return preset != null && (NEON_DB_TOKENS_BACKED_PRESETS as readonly string[]).includes(preset);
}

export function isUnifiedDiscoveryPreset(
  preset: string | undefined,
): preset is (typeof UNIFIED_DISCOVERY_PRESETS)[number] {
  return preset != null && (UNIFIED_DISCOVERY_PRESETS as readonly string[]).includes(preset);
}

export const presets = [
  'popular',
  'trending',
  'social',
  'launchpads',
  ...aggregatedPresets,
  'stocks',
] as const;

export const timeframes = ['5m', '1', '4', '12', '24'] as const;

export const socialLaunchpads = [
  LaunchpadId.BASEAPP,
  LaunchpadId.ZORA_CREATOR,
  LaunchpadId.ZORA,
] as const;

export const otherLaunchpads = [
  LaunchpadId.BAGS,
  LaunchpadId.BANKR,
  LaunchpadId.BELIEVE,
  LaunchpadId.BOWFUN,
  LaunchpadId.CLANKER,
  LaunchpadId.FLAP,
  LaunchpadId.FOUR_MEME,
  LaunchpadId.HOOD_FUN,
  LaunchpadId.JUPITER_STUDIO,
  LaunchpadId.LONG,
  LaunchpadId.MOONSHOT,
  LaunchpadId.O1_EXCHANGE,
  LaunchpadId.PONS,
  LaunchpadId.PRINTR,
  LaunchpadId.PUMP_FUN,
  LaunchpadId.VIRTUAL,
] as const;

const launchpads = [...socialLaunchpads, ...otherLaunchpads] as const;

const numericFilterType = z.object({ min: z.number().optional(), max: z.number().optional() });

export const numericFilters = z.record(z.enum(filterableNumericColumns), numericFilterType);

export const filters = numericFilters.and(
  z.object({
    networks: z.array(z.number()).default(SUPPORTED_CHAIN_IDS),
    launchpads: z.array(z.enum(launchpads)).optional(),
    creator: z.string().optional(),
    currencies: z.array(z.string()).optional(),
    issuers: z.array(z.string()).optional(),
    backings: z.array(z.string()).optional(),
  }),
);

const zOptionalNumber = z.coerce.number().optional();
const zOptionalString = z.coerce.string().optional();
const zOptionalPercentage = z.coerce
  .number()
  .transform((val) => val / 100)
  .optional();

const transformEnumArray =
  (list: readonly string[], errorMessage: string) =>
  (value: string | undefined, ctx: z.RefinementCtx) => {
    const arrayValue = value?.split(',') ?? [];

    if (arrayValue.some((item) => !list.includes(item))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: errorMessage });
    }

    return arrayValue;
  };

const transformNetworksArray = (value: string, ctx: z.RefinementCtx) => {
  const supportedChains = SUPPORTED_CHAIN_IDS.map(String);
  const arrayValue = transformEnumArray(supportedChains, 'Invalid network')(value, ctx);

  if (arrayValue.length === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'At least one network is required' });
  }

  return arrayValue.map(Number);
};

export const zSerializedDiscoveryParameters = z.object({
  timeframe: z.enum(timeframes),
  sortBy: z.enum(sortableColumns),
  sortDirection: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().min(1).default(1),
  preset: z.enum(presets).optional(),
  launchpads: z.string().optional().transform(transformEnumArray(launchpads, 'Invalid launchpads')),
  currencies: z
    .string()
    .optional()
    .transform((value) => value?.split(',') ?? []),
  issuers: z
    .string()
    .optional()
    .transform((value) => value?.split(',') ?? []),
  backings: z
    .string()
    .optional()
    .transform((value) => value?.split(',') ?? []),
  creator: zOptionalString,
  filterLiquidityMin: zOptionalNumber,
  filterLiquidityMax: zOptionalNumber,
  filterMarketCapMin: zOptionalNumber,
  filterMarketCapMax: zOptionalNumber,
  filterVolByTimeMin: zOptionalNumber,
  filterVolByTimeMax: zOptionalNumber,
  filterNumTxsByTimeMin: zOptionalNumber,
  filterNumTxsByTimeMax: zOptionalNumber,
  filterAgeMin: zOptionalNumber,
  filterAgeMax: zOptionalNumber,
  filterNumHoldersMin: zOptionalNumber,
  filterNumHoldersMax: zOptionalNumber,
  filterChange5mMin: zOptionalPercentage,
  filterChange5mMax: zOptionalPercentage,
  filterChange1Min: zOptionalPercentage,
  filterChange1Max: zOptionalPercentage,
  filterChange4Min: zOptionalPercentage,
  filterChange4Max: zOptionalPercentage,
  filterChange12Min: zOptionalPercentage,
  filterChange12Max: zOptionalPercentage,
  filterChange24Min: zOptionalPercentage,
  filterChange24Max: zOptionalPercentage,
  networks: z
    .string()
    .optional()
    .default(SUPPORTED_CHAIN_IDS.join(','))
    .transform(transformNetworksArray),
  pageDiscovery: z
    .string()
    .transform((val) => val === 'true')
    .optional()
    .default('false'),
  searchQuery: z
    .string()
    .trim()
    .max(100)
    .optional()
    .transform((val) => (val ? val : undefined)),
});

export const zDiscoveryParameters =
  zSerializedDiscoveryParameters.transform(deserializeDiscoveryState);

export type DiscoveryParametersSerialized = z.input<typeof zSerializedDiscoveryParameters>;
export type DiscoveryParametersDeserialized = z.output<typeof zDiscoveryParameters>;
export type DiscoveryNumericFilter = z.infer<typeof numericFilterType>;
export type DiscoveryFilters = z.infer<typeof filters>;
export type DiscoveryPreset = (typeof presets)[number];
export type DiscoveryTimeframe = (typeof timeframes)[number];
export type DiscoveryLaunchpad = (typeof launchpads)[number];
